<template>
  <v-card flat outlined class="config-card-minimal">
    <!-- Header minimalista -->
    <v-card-title class="px-6 pt-6 pb-4">
      <div class="d-flex align-center">
        <div class="config-icon-container mr-3">
          <v-icon size="20">mdi-cog-outline</v-icon>
        </div>
        <div>
          <h2 class="text-h6 font-weight-medium mb-0">Configuración de Suscripciones</h2>
          <p class="text-caption text--secondary mb-0 mt-1">
            Parámetros de gestión automática de pagos
          </p>
        </div>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="px-6 py-6">
      <!-- Grid de configuración en 3 columnas -->
      <v-row class="config-grid">
        <!-- Intentos de Pago -->
        <v-col cols="12" md="4">
          <configuration-field
            v-model="localConfig.paymentRetries"
            label="Intentos de pago"
            icon="mdi-credit-card-refresh-outline"
            :min="1"
            :max="5"
            placeholder="3"
            hint="1-5 intentos de cobro"
            :disabled="saving"
            :rules="[rules.required, rules.minRetries, rules.maxRetries]"
            show-controls
          />
        </v-col>

        <!-- Período de Gracia -->
        <v-col cols="12" md="4">
          <configuration-field
            v-model="localConfig.gracePeriodDays"
            label="Período de gracia"
            icon="mdi-clock-outline"
            :min="0"
            :max="30"
            placeholder="7"
            suffix="días"
            hint="0-30 días de espera"
            :disabled="saving"
            :rules="[rules.required, rules.minDays, rules.maxDays]"
          />
        </v-col>

        <!-- Cancelación Automática -->
        <v-col cols="12" md="4">
          <configuration-field
            v-model="localConfig.autoCancelEnabled"
            label="Cancelación automática"
            icon="mdi-cancel"
            type="select"
            :items="autoCancelOptions"
            :disabled="saving"
            :hint="localConfig.autoCancelEnabled ? 'Al agotar intentos' : 'Requiere acción manual'"
          />
        </v-col>
      </v-row>

      <!-- Resumen compacto -->
      <v-card flat class="summary-card mt-6 pa-4">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div class="summary-item">
            <span class="summary-label">Total aproximado:</span>
            <span class="summary-value ml-2">~{{ totalDays }} días</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Proceso:</span>
            <span class="summary-value ml-2">
              {{ localConfig.paymentRetries }} intento{{ localConfig.paymentRetries > 1 ? 's' : '' }} 
              → {{ localConfig.gracePeriodDays }}d gracia
              → {{ localConfig.autoCancelEnabled ? 'Cancelación' : 'Revisión' }}
            </span>
          </div>
        </div>
      </v-card>

      <!-- Nota informativa compacta -->
      <div class="info-note mt-4 pa-3 d-flex align-start">
        <v-icon small class="mr-2 mt-1" color="grey darken-1">mdi-information-outline</v-icon>
        <div class="text-caption text--secondary">
          Los miembros recibirán notificaciones automáticas en cada etapa del proceso. 
          La configuración se aplica a todas las suscripciones activas.
        </div>
      </div>
    </v-card-text>

    <v-divider></v-divider>

    <!-- Acciones minimalistas -->
    <v-card-actions class="px-6 py-4">
      <v-btn
        text
        small
        @click="$emit('reset')"
        :disabled="saving"
        class="text-caption"
      >
        <v-icon small left>mdi-restore</v-icon>
        Valores predeterminados
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        depressed
        dark
        color="grey darken-3"
        :loading="saving"
        :disabled="saving || !isConfigValid"
        @click="$emit('save', localConfig)"
        class="px-6"
      >
        <v-icon small left>mdi-check</v-icon>
        Guardar cambios
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import ConfigurationField from './ConfigurationField.vue';

export default {
  name: 'ConfigurationCard',

  components: {
    ConfigurationField
  },
  
  props: {
    config: {
      type: Object,
      required: true
    },
    saving: {
      type: Boolean,
      default: false
    },
    autoCancelOptions: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      localConfig: { ...this.config },
      rules: {
        required: v => !!v || v === 0 || 'Campo requerido',
        minDays: v => v >= 0 || 'Mínimo 0 días',
        maxDays: v => v <= 30 || 'Máximo 30 días',
        minRetries: v => v >= 1 || 'Mínimo 1 intento',
        maxRetries: v => v <= 5 || 'Máximo 5 intentos',
      }
    };
  },

  computed: {
    isConfigValid() {
      return (
        this.localConfig.paymentRetries >= 1 &&
        this.localConfig.paymentRetries <= 5 &&
        this.localConfig.gracePeriodDays >= 0 &&
        this.localConfig.gracePeriodDays <= 30
      );
    },

    totalDays() {
      // Estima días totales (asumiendo ~3 días entre intentos)
      const retryDays = (this.localConfig.paymentRetries - 1) * 3;
      return retryDays + this.localConfig.gracePeriodDays;
    }
  },

  watch: {
    config: {
      handler(newVal) {
        this.localConfig = { ...newVal };
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.config-card-minimal {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #ffffff;
  transition: all 0.2s ease;
}

.config-card-minimal:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* Header icon container */
.config-icon-container {
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Summary card */
.summary-card {
  background: #fafafa !important;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.summary-item {
  display: flex;
  align-items: center;
  margin: 4px 8px;
}

.summary-label {
  font-size: 0.75rem;
  color: #757575;
  font-weight: 500;
}

.summary-value {
  font-size: 0.8125rem;
  color: #212121;
  font-weight: 600;
}

/* Info note */
.info-note {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

/* Transiciones suaves */
* {
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
</style>
